/**
 * Daily Challenges Page - Main Orchestrator
 * Complete daily challenges to earn XP and level up
 */

import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

// Hooks
import { useChallengesData } from './hooks/useChallengesData';

// Components
import {
  ChallengesHero,
  LevelProgressCard,
  ShareChallengesCard,
  BadgeCollectionCard,
  OtherChallengesCard,
  ActivityCalendar,
  ProTipsCard,
  LevelUpModal,
} from './components';

export default function ChallengesPage() {
  const { t } = useLanguage();

  // Fetch all challenges data
  const {
    stats,
    levelInfo,
    heroStats,
    badges,
    earnedBadges,
    sharesToday,
    canShare,
    shareXpRewards,
    recordShare,
    isLoading,
    error,
    showLevelUpModal,
    levelUpData,
    dismissLevelUpModal,
  } = useChallengesData();

  // Page title
  useEffect(() => {
    document.title = t('challenges.pageTitle', 'Daily Challenges - Rumio');
  }, [t]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-500">{t('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">!</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('common.error', 'Something went wrong')}
          </h2>
          <p className="text-gray-500 mb-4">
            {t('challenges.loadError', 'Unable to load your challenges. Please try again.')}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t('common.retry', 'Try Again')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <ChallengesHero stats={heroStats} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-4 space-y-6">
        {/* Level Progress Card - NEW from mobile */}
        {levelInfo && <LevelProgressCard levelInfo={levelInfo} />}

        {/* Share Challenges */}
        <ShareChallengesCard
          sharesToday={sharesToday}
          shareXpRewards={shareXpRewards}
          canShare={canShare}
          onShare={recordShare}
        />

        {/* Badge Collection - NEW from mobile */}
        <BadgeCollectionCard badges={badges} earnedBadges={earnedBadges} />

        {/* Other Challenges */}
        <OtherChallengesCard
          totalScans={stats?.totalScans ?? 0}
          totalReferrals={stats?.totalReferrals ?? 0}
          streak={stats?.streak ?? 0}
        />

        {/* Activity Calendar */}
        <ActivityCalendar />

        {/* Pro Tips */}
        <ProTipsCard />
      </div>

      {/* Level Up Modal - NEW from mobile */}
      <LevelUpModal
        isOpen={showLevelUpModal}
        levelUpData={levelUpData}
        onClose={dismissLevelUpModal}
      />
    </div>
  );
}
